import React from 'react'
import PropTypes from 'prop-types'
import { Picker } from '../Picker/Picker'

/**
 * Bronson TimePicker component.
 * @see https://bronson.vwfs.tools/bluelabel/components/detail/bronson-timepicker.html
 *
 * @param {string} [value] - Time value
 * @param {string} [name] - Name attribute of input element
 * @param {string} [dateFormat='H:i'] - Time format used by flatpickr, @see https://flatpickr.js.org/options/
 * @param {string} [placeholder] - Placeholder shown when no date is selected
 * @param {string} [locale] - Sets locale of date picker labels, one of ['pl', 'de', 'it', 'cs', 'es', 'fr', 'nl', 'pt', 'ja', 'sk'] (ISO-639-1 code)
 * @param {boolean} [noBackground=true] - By default the addon has a background. To remove the background and only show the addon text and/or icon.
 * @param {boolean} [disabled=false] - Disabled state
 * @param {function} [onBlur] - onBlur handler
 * @param {function} [onChange] - onChange handler
 * @param {function} [onError] - Handler called upon internal {@link Picker} error.
 * @param {boolean} [error=false] - Error state
 * @param {object} [flatpickrOptions] - Additional flatpickr options
 * @param {object} [flatpickrProps] - Additional props passed to Flatpickr React component
 * @param {string} [className=''] - Additional CSS classes on Flatpickr element (div with class c-input)
 * @param {string} [pickerClassName=''] - Additional CSS classes on input element of Flatpickr (input with class c-input__input)
 * @param {string} [testId] - data-testid attribute on table
 * @return {JSX.Element} - The TimePicker element.
 */
export function TimePicker({
  className = '',
  dateFormat = 'H:i',
  disabled = false,
  error = false,
  flatpickrOptions,
  flatpickrProps,
  id,
  locale,
  name,
  noBackground = true,
  onBlur,
  onChange,
  onError,
  placeholder,
  time24hr = false,
  timePickerClassName = '',
  value,
  testId,
  ...otherProps
}) {
  return (
    <Picker
      className={className}
      componentClassName="c-timepicker js-timepicker"
      dateFormat={dateFormat}
      disabled={disabled}
      error={error}
      flatpickrOptions={{
        ...flatpickrOptions,
        enableTime: true,
        noCalendar: true,
        time_24hr: time24hr,
      }}
      flatpickrProps={flatpickrProps}
      id={id}
      locale={locale}
      maskedInput={false}
      mode="single"
      name={name}
      noBackground={noBackground}
      onBlur={onBlur}
      onChange={onChange}
      onError={onError}
      pickerClassName={timePickerClassName}
      placeholder={placeholder}
      value={value}
      testId={testId}
      {...otherProps}
    />
  )
}

TimePicker.propTypes = {
  className: PropTypes.string,
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  flatpickrOptions: PropTypes.oneOfType([PropTypes.object]),
  flatpickrProps: PropTypes.oneOfType([PropTypes.object]),
  id: PropTypes.string,
  locale: PropTypes.oneOf(['pl', 'de', 'it', 'cs', 'es', 'fr', 'nl', 'pt', 'ja', 'sk']), // ISO-639-1 code
  mask: PropTypes.string,
  name: PropTypes.string,
  noBackground: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  placeholder: PropTypes.string,
  time24hr: PropTypes.bool,
  timePickerClassName: PropTypes.string,
  value: PropTypes.string,
  testId: PropTypes.string,
}
